<template>
  <div class="relative flex items-center mt-[3vw]">
    <!-- 左侧翻页按钮 -->
    <button @click="prevPage" :disabled="currentPage === 1"
      class="absolute z-10 w-[70px] h-[150px] bg-[#F3F8FF] shadow-md hover:bg-[#D4E4FA] disabled:opacity-50 disabled:cursor-not-allowed">
      <img src="@/assets/left.png" alt="left" class="h-[28px] w-[12px] object-cover shadow-lg" />
    </button>

    <!-- 图片网格 -->
    <div class="w-[81vw] mx-auto gap-[10px] grid " :class="[columnClass]">
      <div v-for="(image, index) in paginatedImages" :key="index"
        class="relative overflow-hidden flex items-center justify-center"
        :class="props.imageHeightClass || 'h-[210px]'">

        <img :src="image.src" :alt="image.alt"
          class="max-h-full max-w-full object-cover transition-transform duration-300 hover:scale-105 cursor-pointer"
          @click="showImageOverlay(image)" />

        <!-- Caption 仅在有内容时显示，且一直可见 -->
        <div v-if="image.caption"
          class="absolute bottom-0 left-0 right-0 p-2 font-medium bg-black bg-opacity-50 text-white text-[1vw] leading-[1.13]">
          {{ image.caption }}
        </div>
      </div>
    </div>

    <!-- 右侧翻页按钮 -->
    <button @click="nextPage" :disabled="currentPage === totalPages"
      class="absolute right-0 z-10 w-[70px] h-[150px] bg-[#F3F8FF] shadow-md hover:bg-[#D4E4FA] disabled:opacity-50 disabled:cursor-not-allowed">
      <img src="@/assets/right.png" alt="right" class="h-[28px] w-[12px] object-cover shadow-lg" />
    </button>

    <!-- 大图预览 -->
    <div v-if="hoveredImage" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70"
      @click.self="closeImageOverlay">
      <button @click="prevImage" :disabled="hoveredImageIndex === 0"
        class="absolute left-4 text-white bg-black bg-opacity-50 rounded-full  p-4 hover:bg-opacity-80 disabled:opacity-30">
        <img src="@/assets/left.png" class="w-4 h-6 pointer-events-none z-101 " />
      </button>
      <div class="relative max-w-[80vw] max-h-[80vh] flex items-center justify-center">
        <img :src="hoveredImage.src" :alt="hoveredImage.alt"
          class="max-w-full max-h-full object-contain rounded shadow-lg" />
        <button @click="closeImageOverlay"
          class="absolute top-2 right-2 text-white bg-black bg-opacity-50 rounded-full w-8 h-8 flex items-center justify-center hover:bg-opacity-80">
          ✕
        </button>
      </div>
      <button @click="nextImage" :disabled="hoveredImageIndex === imageItems.length - 1"
        class="absolute right-4 text-white bg-black bg-opacity-50 z-999 rounded-full p-4 hover:bg-opacity-80 disabled:opacity-30">
        <img src="@/assets/right.png" class="w-4 h-6 pointer-events-none  " />
      </button>
    </div>
  </div>

  <!-- 分页圆点 -->
  <div class="flex justify-center mt-[1.8vw] mb-[1.8vw] space-x-3">
    <span v-for="page in totalPages" :key="page" @click="goToPage(page)" class="w-3 h-3 rounded-full cursor-pointer"
      :class="{ 'bg-blue-500': page === currentPage, 'bg-gray-300': page !== currentPage }"></span>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';

const imagesPerPage = computed(() => props.imagesPerPage || 12);

interface ImageItem {
  src: string;
  alt: string;
  caption: string;
}

const props = defineProps<{
  imageItems: ImageItem[];
  imageHeightClass?: string;
  columns?: number;
  imagesPerPage?: number;
}>();

const columnClass = computed(() => {
  const col = props.columns || 4;
  return {
    'grid-cols-1': col === 1,
    'grid-cols-2': col === 2,
    'grid-cols-3': col === 3,
    'grid-cols-4': col === 4,
    'grid-cols-5': col === 5,
    'grid-cols-6': col === 6,
  };
});

const currentPage = ref(1);
const totalPages = computed(() => Math.ceil(props.imageItems.length / imagesPerPage.value));

const paginatedImages = computed(() => {
  const startIndex = (currentPage.value - 1) * imagesPerPage.value;
  return props.imageItems.slice(startIndex, startIndex + imagesPerPage.value);
});

const hoveredImageIndex = ref<number | null>(null);
const hoveredImage = computed(() => hoveredImageIndex.value !== null ? props.imageItems[hoveredImageIndex.value] : null);

const showImageOverlay = (image: ImageItem) => {
  const index = props.imageItems.findIndex(i => i.src === image.src);
  if (index !== -1) hoveredImageIndex.value = index;
};

const closeImageOverlay = () => {
  hoveredImageIndex.value = null;
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) currentPage.value++;
};

const prevPage = () => {
  if (currentPage.value > 1) currentPage.value--;
};

const goToPage = (page: number) => {
  currentPage.value = page;
};

const prevImage = () => {
  if (hoveredImageIndex.value !== null && hoveredImageIndex.value > 0) hoveredImageIndex.value--;
};

const nextImage = () => {
  if (hoveredImageIndex.value !== null && hoveredImageIndex.value < props.imageItems.length - 1) hoveredImageIndex.value++;
};
</script>